import { htmlToPlainText } from '../core/html-conversion';
import { sanitizeHtml } from '../core/sanitize';
import type { SourceFormat } from '../types/app';

export interface ImportedDocument {
  plainText: string;
  rawHtml: string;
  source: Exclude<SourceFormat, 'auto'>;
  label: string;
}

const maxDocumentBytes = 25 * 1024 * 1024;
const maxTextBytes = 5 * 1024 * 1024;

function readAsArrayBuffer(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error('Die Datei konnte nicht gelesen werden.'));
    });
    reader.addEventListener('error', () => reject(new Error('Die Datei konnte nicht gelesen werden.')));
    reader.readAsArrayBuffer(file);
  });
}

function readAsText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(typeof reader.result === 'string' ? reader.result : ''));
    reader.addEventListener('error', () => reject(new Error('Die Datei konnte nicht gelesen werden.')));
    reader.readAsText(file, 'UTF-8');
  });
}

function extensionOf(filename: string): string {
  return filename.toLocaleLowerCase('de-DE').split('.').pop() ?? '';
}

async function importPdf(file: File): Promise<ImportedDocument> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(await readAsArrayBuffer(file)) });
  const document = await loadingTask.promise;
  const pages: string[] = [];
  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();
    let pageText = '';
    for (const item of content.items) {
      if (!('str' in item)) continue;
      pageText += item.str;
      pageText += 'hasEOL' in item && item.hasEOL ? '\n' : ' ';
    }
    pages.push(pageText.replace(/[ \t]+\n/g, '\n').replace(/[ \t]{2,}/g, ' ').trim());
    page.cleanup();
  }
  await loadingTask.destroy();
  const plainText = pages.join('\n\n').trim();
  if (!plainText) {
    throw new Error('Diese PDF enthält keinen auslesbaren Text. Für gescannte Bild-PDFs ist zuerst eine OCR-Texterkennung nötig.');
  }
  return { plainText, rawHtml: '', source: 'pdf', label: `PDF „${file.name}“` };
}

async function importDocx(file: File): Promise<ImportedDocument> {
  const mammoth = await import('mammoth');
  const result = await mammoth.convertToHtml({ arrayBuffer: await readAsArrayBuffer(file) });
  const rawHtml = sanitizeHtml(result.value);
  const plainText = htmlToPlainText(rawHtml);
  if (!plainText.trim()) throw new Error('Die Word-Datei enthält keinen auslesbaren Text.');
  return { plainText, rawHtml, source: 'office', label: `Word-Datei „${file.name}“` };
}

export async function importLocalFile(file: File): Promise<ImportedDocument> {
  const extension = extensionOf(file.name);
  const supported = ['txt', 'md', 'markdown', 'html', 'htm', 'pdf', 'docx'];
  if (!supported.includes(extension)) throw new Error('Unterstützt werden PDF, DOCX, HTML, Markdown und TXT.');
  const limit = ['txt', 'md', 'markdown', 'html', 'htm'].includes(extension) ? maxTextBytes : maxDocumentBytes;
  if (file.size > limit) throw new Error(`Die Datei ist zu groß. Erlaubt sind höchstens ${Math.round(limit / 1024 / 1024)} MB.`);
  if (extension === 'pdf') return importPdf(file);
  if (extension === 'docx') return importDocx(file);

  const value = await readAsText(file);
  if (extension === 'html' || extension === 'htm') {
    const rawHtml = sanitizeHtml(value);
    return { plainText: htmlToPlainText(rawHtml), rawHtml, source: 'html', label: `HTML-Datei „${file.name}“` };
  }
  const source = extension === 'md' || extension === 'markdown' ? 'markdown' : 'plain';
  return { plainText: value, rawHtml: '', source, label: `${source === 'markdown' ? 'Markdown' : 'Textdatei'} „${file.name}“` };
}
